import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { ChevronDownIcon } from "../icons"; // Assuming this exists
import { useSidebar } from "../context/SidebarContext";
import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
import {
    BriefcaseIcon,
    UserIcon,
    FileTextIcon,
    SettingsIcon,
    HomeIcon,
    ArrowBigRight,
    NotebookIcon,
    SearchCheckIcon,
    UserCircle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

type NavItem = {
    name: string;
    icon: React.ReactNode;
    path?: string;
    subItems?: { name: string; icon?: React.ReactNode; path: string; pro?: boolean; new?: boolean }[];
};

// Applicant-specific navigation items
const navItems: NavItem[] = [
    {
        icon: <HomeIcon className="w-5 h-5" />,
        name: "Dashboard",
        path: "/applicant/dashboard",
    },
    {
        icon: <SearchCheckIcon className="w-5 h-5" />,
        name: "Explore Jobs",
        path: "/applicant/explore",
    },
    {
        icon: <BriefcaseIcon className="w-5 h-5" />,
        name: "Applied Jobs",
        path: "/applicant/applied",
    },
    {
        icon: <NotebookIcon className="w-5 h-5" />,
        name: "My Order",
        path: "/applicant/order",
    },
];

const ApplicantSidebar: React.FC = () => {
    // Ensure setIsHovered is available from your context, or add onMouseEnter/Leave logic
    const { isExpanded, isMobileOpen, isHovered, toggleSidebar, setIsHovered } = useSidebar();
    const location = useLocation();

    const [openSubmenu, setOpenSubmenu] = useState<{
        type: "Applicant";
        index: number;
    } | null>(null);

    const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({});
    const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Determine if the sidebar is visually open (Expanded, Mobile Open, or Hovered)
    const isSidebarOpen = isExpanded || isMobileOpen || isHovered;

    const { user, fetchUser, logout } = useAuth();

    const isActive = useCallback(
        (path: string) =>
            location.pathname === path ||
            location.pathname.startsWith(path + "/"),
        [location.pathname]
    );

    const isSubItemActive = (nav: NavItem): boolean => {
        if (!nav.subItems) return false;
        return nav.subItems.some(
            (subItem) => isActive(subItem.path)
        );
    };

    // Auto-open submenu based on current route
    useEffect(() => {
        let submenuMatched = false;
        navItems.forEach((nav, index) => {
            if (nav.subItems) {
                nav.subItems.forEach((subItem) => {
                    if (isActive(subItem.path)) {
                        setOpenSubmenu({ type: "Applicant", index });
                        submenuMatched = true;
                    }
                });
            }
        });
        if (!submenuMatched) setOpenSubmenu(null);
    }, [location, isActive]);

    // Calculate height for submenu animation
    useEffect(() => {
        if (openSubmenu !== null) {
            const key = `${openSubmenu.type}-${openSubmenu.index}`;
            if (subMenuRefs.current[key]) {
                setSubMenuHeight((prev) => ({
                    ...prev,
                    [key]: subMenuRefs.current[key]?.scrollHeight || 0,
                }));
            }
        }
    }, [openSubmenu]);

    const handleSubmenuToggle = (index: number) => {
        setOpenSubmenu((prev) =>
            prev && prev.index === index ? null : { type: "Applicant", index }
        );
    };

    const renderMenuItems = () => (
        <ul className="flex flex-col gap-1 text-sm">
            {navItems.map((nav, index) => (
                <li key={nav.name}>
                    {nav.subItems ? (
                        /* Parent Item with Submenu */
                        <button
                            onClick={() => {
                                if (!isExpanded && !isHovered) toggleSidebar();
                                handleSubmenuToggle(index);
                            }}
                            className={`flex items-center justify-between w-full px-3 py-2.5 rounded-md transition-all duration-200 group
                                ${isSubItemActive(nav)
                                    ? "bg-green-50 brand-text dark:bg-gray-800 dark:text-green-400 font-semibold"
                                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                                }`}
                        >
                            <div className={`flex items-center gap-3 ${!isSidebarOpen ? "w-full justify-center" : ""}`}>
                                <span className={`${isSubItemActive(nav) ? "brand-text" : "text-gray-500 group-hover:text-gray-900"}`}>
                                    {nav.icon}
                                </span>
                                {isSidebarOpen && <span className="whitespace-nowrap">{nav.name}</span>}
                            </div>

                            {isSidebarOpen && (
                                <ChevronDownIcon
                                    className={`w-4 h-4 transition-transform duration-200
                                    ${openSubmenu?.index === index ? "rotate-180 brand-text" : "text-gray-400"}`}
                                />
                            )}
                        </button>
                    ) : (
                        /* Standard Link Item */
                        nav.path && (
                            <Link
                                to={nav.path}
                                onClick={() => {
                                    if (isMobileOpen) toggleSidebar(); // Only close on click in mobile mode
                                }}
                                className={`flex items-center justify-between w-full px-3 py-2.5 rounded-md transition-all duration-200 group
                                    ${isActive(nav.path)
                                        ? "bg-green-50 brand-text dark:bg-gray-800 dark:text-green-400 font-semibold"
                                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                                    }`}
                            >
                                <div className={`flex items-center gap-3 ${!isSidebarOpen ? "w-full justify-center" : ""}`}>
                                    <span className={`${isActive(nav.path) ? "brand-text" : "text-gray-500 group-hover:text-gray-900"}`}>
                                        {nav.icon}
                                    </span>
                                    {isSidebarOpen && <span className="whitespace-nowrap">{nav.name}</span>}
                                </div>
                            </Link>
                        )
                    )}

                    {/* Submenu List */}
                    {nav.subItems && isSidebarOpen && (
                        <div
                            ref={(el) => {
                                subMenuRefs.current[`Applicant-${index}`] = el;
                            }}
                            className="overflow-hidden transition-all duration-300 ease-in-out"
                            style={{
                                height: openSubmenu?.index === index
                                    ? `${subMenuHeight[`Applicant-${index}`]}px`
                                    : "0px",
                            }}
                        >
                            <ul className="mt-1 ml-4 border-l border-gray-200 dark:border-gray-700 pl-2 space-y-1">
                                {nav.subItems.map((subItem) => (
                                    <li key={subItem.name}>
                                        <Link
                                            to={subItem.path}
                                            onClick={() => { if (isMobileOpen) toggleSidebar() }}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-200
                                                ${isActive(subItem.path)
                                                    ? "brand-text bg-green-50/50 font-medium"
                                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                                }`}
                                        >
                                            {/* Optional: Smaller Icon for submenu */}
                                            {/* <span className="w-4 h-4">{subItem.icon}</span> */}
                                            <span>{subItem.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );

    return (
        <aside
            onMouseEnter={() => setIsHovered?.(true)}
            onMouseLeave={() => setIsHovered?.(false)}
            className={`
               fixed lg:static
                top-0 left-0
                h-full 
                bg-white dark:bg-gray-900 
                transition-all duration-300 ease-in-out 
                z-40
                
                ${isSidebarOpen ? "w-[370px]" : "w-[72px]"}

                ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
                lg:translate-x-0
            `}
        >
            <button
                onClick={toggleSidebar}
                className="
                    hidden lg:flex
                    absolute -right-3 top-6
                    items-center justify-center
                    w-6 h-6
                    rounded-full
                    bg-white dark:bg-gray-600
                    border border-gray-300 dark:border-gray-700
                    shadow-md
                    hover:bg-gray-100 dark:hover:bg-gray-700
                    transition
                "
            >
                {isSidebarOpen ? "<" : ">"}
            </button>

            {/* Scrollable Menu Area */}
            <div className="flex flex-1 flex-col h-full overflow-y-auto p-2 gap-1">
                {isSidebarOpen && (
                    <div className="py-4 px-6 bg-gray-700 rounded-md space-y-1">
                        <p className="font-bold text-lg text-white"> {user?.fullName}</p>
                        <p className="text-xl text-gray-400"> {user?.emailId || user.mobileNumber}</p>
                        <Link
                            to="/applicant/profile"
                            onClick={() => { if (isMobileOpen) toggleSidebar() }}
                            className="bg-transparent p-0 brand-text group inline-block"
                        >
                            Edit Profile →
                        </Link>
                    </div>
                )}

                {!isSidebarOpen && (
                    <div className={`py-4 bg-gray-700 rounded-md space-y-1 ${isSidebarOpen ? "px-6" : "px-4"}`}>
                        <UserCircle className="text-gray-400" />
                    </div>
                )}

                <nav className="flex-1 space-y-1 p-2 shadow-md rounded-md bg-white dark:bg-gray-900 dark:shadow-gray-800/60">
                    {renderMenuItems()}
                </nav>

                {/* Footer / Theme Toggle */}
                {/* <div className={`mt-auto px-2 py-6`}>
                    <div className={`flex items-center ${isSidebarOpen ? "justify-left gap-4 px-2" : "justify-center"}`}>
                        <ThemeToggleButton />

                        {isSidebarOpen && (
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 pr-2">
                                Theme
                            </span>
                        )}
                    </div>
                </div> */}
            </div>
        </aside>
    );
};

export default ApplicantSidebar;